import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import styled, { css, keyframes } from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button";
import {
    MessageContent,
    MessageInput,
    isMessageDocEmpty,
    messageDocHasPrepMention,
    mentionCandidates,
} from "components/atoms/message";
import Text from "components/atoms/text/Text";

import Constants from "utils/constants";

// 깜빡임 애니메이션 정의
const blinkBg = keyframes`
    0% { background-color: var(--color-Base2); }
    50% { background-color: var(--color-White); }
    100% { background-color: var(--color-Base2); }
`;

const MessageContainer = styled.div`
    position: relative;

    padding: 10px 12px;
    margin: ${({ $isOwn }) => ($isOwn ? "10px 12px 10px auto" : "10px auto 10px 12px")};
    width: fit-content;
    min-width: min(180px, calc(100% - 32px));
    max-width: min(86%, calc(100% - 32px));
    height: auto;
    background-color: ${({ $isOwn, $role }) =>
        $isOwn ? "#eaf5ff" : $role === Constants.REVIEW_ROLE_AI ? "#f7fbf9" : "var(--color-White)"};
    border: solid 1px
        ${({ $isOwn, $role }) => ($isOwn ? "#c9e1f7" : $role === Constants.REVIEW_ROLE_AI ? "#dcebe5" : "#e1e7ea")};
    border-radius: ${({ $isOwn }) => ($isOwn ? "14px 4px 14px 14px" : "4px 14px 14px 14px")};
    box-shadow: 0 1px 4px rgba(15, 23, 42, 0.05);
    transition:
        background-color 80ms ease-in,
        border-color 80ms ease-in;
    overflow-wrap: anywhere;

    ${({ $isBlinking }) =>
        $isBlinking &&
        css`
            animation: ${blinkBg} 1s linear 0s 2 alternate;
            /* 1.0s * 2 = 2초 */
        `}
`;

const SourceContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
`;

const DivideLine = styled.div`
    display: block;
    margin: 8px 0 0 0;
    width: 100%;
    height: 1px;
    background-color: var(--color-Line);
`;

const SourceSubText = styled.div`
    font-size: 10px;
    font-weight: 400;
    border-radius: 4px;
    background-color: var(--color-InfoBox);
    color: var(--color-Grey1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
        cursor: pointer;
        background-color: var(--color-Base1);
    }
`;

const SourceNumText = styled.span`
    margin: 0 4px 0 4px;
    font-weight: 400;
    color: var(--color-SubBlack);
`;

const MessageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: ${({ $isOwn }) => ($isOwn ? "row-reverse" : "row")};
    align-items: center;
    gap: 10px;

    margin-bottom: 10px;
`;

const MessageHeaderLeft = styled.div`
    display: flex;
    flex-direction: ${({ $isOwn }) => ($isOwn ? "row-reverse" : "row")};
    align-items: center;
    gap: 8px;
`;
const MessageHeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MessageFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: ${({ $isOwn }) => ($isOwn ? "row-reverse" : "row")};
    gap: 8px;
`;

const MessageFooterLeft = styled.div`
    display: flex;
    gap: 8px;
`;
const MessageFooterRight = styled.div`
    display: flex;
    gap: 8px;
`;

const MessageVersionText = styled(Text)`
    display: flex;
    align-items: center;

    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const ProfileIcon = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 24px;
    width: 24px;
    height: 24px;
    background-color: var(--color-Base1);
    border-radius: 12px;
    border: 1px solid lightgray;
`;

const ProfileText = styled(Text)`
    display: flex;
    align-items: center;

    font-size: 1.2rem;
    color: ${({ $role }) => ($role === Constants.REVIEW_ROLE_HUMAN ? "#4d7194" : "var(--color-Key)")};
    background-color: ${({ $role }) => ($role === Constants.REVIEW_ROLE_HUMAN ? "#edf6ff" : "#e8f7f2")};

    padding: 0 4px;
    border-radius: 4px;
`;

const MemoBadge = styled(Text)`
    display: flex;
    align-items: center;

    font-size: 1.2rem;
    color: var(--color-Grey1);
    background-color: var(--color-HighlightYellow);

    padding: 0 4px;
    border-radius: 4px;
`;

const MessageDateText = styled(Text)`
    display: flex;
    align-items: center;
    flex-shrink: 0;

    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const MessageBody = styled.div`
    margin-bottom: 12px;
    min-width: 0;
`;

const SelectionPreview = styled.button`
    position: relative;
    width: 100%;
    margin: 0 0 10px 0;
    padding: 8px 10px 8px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    border: 1px solid var(--color-Line);
    border-left: 4px solid ${({ $color }) => $color || "var(--color-Key)"};
    border-radius: 4px;
    background-color: var(--color-Base1);
    cursor: pointer;
    text-align: left;

    &:hover {
        background-color: var(--color-ListHover);
    }
`;

const SelectionPreviewHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const SelectionPreviewTitle = styled(Text)`
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--color-SubBlack);
`;

const SelectionPreviewMeta = styled(Text)`
    font-size: 1.1rem;
    color: var(--color-Grey1);
`;

const SelectionPreviewText = styled(Text)`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 1.2rem;
    line-height: 1.5;
    color: var(--color-SubBlack);
`;

const SelectionPreviewImage = styled.img`
    width: 100%;
    max-height: 120px;
    object-fit: contain;
    object-position: left center;
    border: 1px solid var(--color-Line);
    border-radius: 3px;
    background-color: var(--color-White);
`;

const MessageText = styled(Text)`
    font-size: 1.4rem;
    color: var(--color-SubBlack);
    white-space: pre-line;
    text-align: left;
    line-height: 1.55;
    word-break: break-word;
`;

const MessageDeletedText = styled(MessageText)`
    color: var(--color-Grey2);
`;

const NoMessageContainer = styled.div`
    padding: 16px;
    color: #888;
`;

function PDFChatMessage(props) {
    const { space_uuid } = useParams();
    const navigate = useNavigate();
    const navigateInSpace = (path) => navigate(`/${space_uuid}${path}`);
    const parseDate = (datetime) => {
        return datetime.split(" ")[0].replaceAll("-", ".");
    };
    const parseTime = (datetime) => {
        return datetime.split(" ")[1].substring(0, "HH:mm".length);
    };

    // region: 포커스 메시지 처리
    const focusedItemRef = useRef();
    const [isFocusedBlinking, setFocusedIsBlinking] = useState(false);
    useEffect(() => {
        if (props.focused) {
            setFocusedIsBlinking(true);
            if (focusedItemRef.current) {
                focusedItemRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            // 3초 후 깜빡임 해제
            const timer = setTimeout(() => setFocusedIsBlinking(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [props.focused]);

    // region: PDF 보기로 이동
    const openPDFReview = (page) => {
        const query = new URLSearchParams({
            view: "pdf",
            page: String(page),
        });
        if (props.message.thread) {
            query.set("thread", String(props.message.thread));
        }
        if (props.message.id) {
            query.set("message", String(props.message.id));
        }
        navigateInSpace(`/reference/${props.reference.reference_no}?${query.toString()}`);
    };

    const normalizeSelectionText = (text) => {
        if (!text) return "";
        return text
            .replace(/\s+/g, " ")
            .replace(/\s+([,.;:!?%])/g, "$1")
            .replace(/([([{])\s+/g, "$1")
            .trim();
    };

    const getSelectionPreviewText = (selection) => {
        if (!selection) return "";
        if (selection.selection_type === "text") {
            const textSpans = selection.data?.text_spans || [];
            const selectedText = Array.isArray(textSpans)
                ? textSpans
                      .map((span) => span?.text)
                      .filter(Boolean)
                      .join(" ")
                : "";
            return normalizeSelectionText(selectedText) || "선택한 텍스트";
        }

        return "PDF에서 선택한 영역";
    };

    const handleReviewSelectionClick = (event) => {
        const reviewSelection = props.message.review_selection;
        if (!reviewSelection) return;

        event.preventDefault();
        event.stopPropagation();
        if (props.setCurPage && props.setSearchBox) {
            // PDF 보기 화면에서 접근한 경우
            props.setCurPage(reviewSelection.page);
            props.setSearchBox({ ...reviewSelection });
        } else {
            // 메타데이터/채팅 상세 화면에서 접근한 경우
            openPDFReview(reviewSelection.page);
        }
    };

    // region: 메시지 수정
    const [isMessageEditing, setIsMessageEditing] = useState(false);
    const [editMessageText, setEditMessageText] = useState();
    const editMesssageTextRef = useRef();
    const handleEditMessageTextUpdate = () => {
        if (isMessageDocEmpty(editMessageText)) {
            return;
        }
        setIsMessageEditing(false);

        let req_message = {
            autoResponse: props.autoResponse,
            content: editMessageText,
            reviewSelectionId: props.message.review_selection?.id,
            reviewSelectionColor: props.message.review_selection_color,
            editMessageId: props.message.id,
        };
        props.chatStart(req_message);
    };

    if (!props.message) {
        return <NoMessageContainer />;
    }

    const getMessageAuthorId = (msg) => {
        if (!msg) return 0;
        return msg.space_member || 0;
    };

    // region: 메시지 랜더링
    const isOwnMessage =
        props.message.role === Constants.REVIEW_ROLE_HUMAN &&
        Number(getMessageAuthorId(props.message)) === Number(props.myProfileId);

    return (
        <MessageContainer
            ref={focusedItemRef}
            $isBlinking={isFocusedBlinking}
            $role={props.message.role}
            $isOwn={isOwnMessage}
        >
            <MessageHeader $isOwn={isOwnMessage}>
                <MessageHeaderLeft $isOwn={isOwnMessage}>
                    {props.message.role === Constants.REVIEW_ROLE_HUMAN && (
                        <>
                            <ProfileIcon>
                                {(() => {
                                    const profile = (props.profiles || []).find(
                                        (p) => p.id === getMessageAuthorId(props.message),
                                    );
                                    return profile?.image_url ? (
                                        <img
                                            src={
                                                process.env.REACT_APP_ENV === "local"
                                                    ? `http://${process.env.REACT_APP_API_BASE_URL}${profile.image_url}`
                                                    : profile.image_url
                                            }
                                            alt={profile.name}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                borderRadius: "50%",
                                            }}
                                        />
                                    ) : (
                                        <Icon name={"userFill"} color={"var(--color-SubBlack)"} />
                                    );
                                })()}
                            </ProfileIcon>
                            <ProfileText $role={props.message.role}>
                                {(() => {
                                    const profile = (props.profiles || []).find(
                                        (p) => p.id === getMessageAuthorId(props.message),
                                    );
                                    return profile ? `@${profile.name}` : "";
                                })()}
                            </ProfileText>
                            {!messageDocHasPrepMention(props.message.content) && (
                                <MemoBadge>
                                    <Tooltip message="AI 응답 요청(@PREP)이 없는 메시지입니다.">
                                        <span>Memo</span>
                                    </Tooltip>
                                </MemoBadge>
                            )}
                        </>
                    )}
                    {props.message.role === Constants.REVIEW_ROLE_AI && (
                        <>
                            <ProfileIcon>
                                <Icon name={"aiResponse"} size={"12px"} color={"var(--color-Key)"} />
                            </ProfileIcon>
                            <ProfileText $role={props.message.role}>@PREP</ProfileText>
                        </>
                    )}
                </MessageHeaderLeft>
                <MessageHeaderRight>
                    <MessageDateText>
                        {parseDate(props.message.created_at) + " " + parseTime(props.message.created_at)}
                    </MessageDateText>
                </MessageHeaderRight>
            </MessageHeader>

            <MessageBody>
                {isMessageEditing ? (
                    <MessageInput
                        ref={editMesssageTextRef}
                        autoFocus
                        value={editMessageText}
                        onChange={setEditMessageText}
                        onSubmit={handleEditMessageTextUpdate}
                        placeholder={"메시지 내용을 입력하세요."}
                        mentions={{ enabled: true, data: mentionCandidates }}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => {
                            // '저장' 버튼을 누를 수 있기 때문에 약간의 딜레이 추가
                            setTimeout(() => setIsMessageEditing(false), 200);
                        }}
                        showSubmit={false}
                        submitDisabled={isMessageDocEmpty(editMessageText)}
                    />
                ) : (
                    <>
                        {props.message.is_deleted ? (
                            <MessageDeletedText>삭제된 메시지입니다.</MessageDeletedText>
                        ) : (
                            <>
                                {props.message.review_selection && (
                                    <SelectionPreview
                                        type="button"
                                        $color={props.message.review_selection_color}
                                        onClick={handleReviewSelectionClick}
                                    >
                                        <SelectionPreviewHeader>
                                            <Icon
                                                name={
                                                    props.message.review_selection.selection_type === "text"
                                                        ? "quote"
                                                        : "box"
                                                }
                                                size="12"
                                                color={props.message.review_selection_color || "var(--color-Key)"}
                                            />
                                            <SelectionPreviewTitle>선택 영역</SelectionPreviewTitle>
                                            <SelectionPreviewMeta>
                                                p.{props.message.review_selection.page} ·{" "}
                                                {props.message.review_selection.selection_type === "text"
                                                    ? "텍스트"
                                                    : "영역"}
                                            </SelectionPreviewMeta>
                                        </SelectionPreviewHeader>
                                        {props.message.review_selection.selection_type === "area" &&
                                        props.message.review_selection_image ? (
                                            <SelectionPreviewImage
                                                src={
                                                    "data:image/png;base64," +
                                                    props.message.review_selection_image.base64
                                                }
                                                alt="PDF 선택 영역"
                                            />
                                        ) : (
                                            <SelectionPreviewText>
                                                {getSelectionPreviewText(props.message.review_selection)}
                                            </SelectionPreviewText>
                                        )}
                                    </SelectionPreview>
                                )}

                                <MessageText>
                                    <MessageContent content={props.message.content} />
                                    {props.streamingMessageIds &&
                                        props.streamingMessageIds.includes(props.message.id) && <AiCursor />}
                                </MessageText>

                                {/* 출처 */}
                                {props.message.sources && props.message.sources.length > 0 && (
                                    <>
                                        <DivideLine />
                                        <SourceContent>
                                            {props.message.sources.map((source, index) => {
                                                return (
                                                    <SourceSubText
                                                        key={index}
                                                        onClick={(e) => {
                                                            if (props.setCurPage) {
                                                                // PDF 보기 화면에서 접근한 경우
                                                                props.setCurPage(source.source_page);
                                                            } else {
                                                                // 메타데이터/채팅 상세 화면에서 접근한 경우
                                                                openPDFReview(source.source_page);
                                                            }
                                                        }}
                                                    >
                                                        <SourceNumText>p.{source.source_page}</SourceNumText>
                                                        {source.source_text}
                                                    </SourceSubText>
                                                );
                                            })}
                                        </SourceContent>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </MessageBody>

            <MessageFooter $isOwn={isOwnMessage}>
                {props.versionCount ? (
                    // 검색된 메시지가 아니면, props.versionCount 가 있음
                    <>
                        <MessageFooterLeft>
                            <Tooltip message={"이전 버전"}>
                                <Button
                                    onlyIcon
                                    bgColor={"transparent"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={() => {
                                        if (props.message.version > 1) {
                                            props.onChangeVersion(-1);
                                        }
                                    }}
                                >
                                    <Icon
                                        name={"arrowLeft"}
                                        size={"10"}
                                        color={props.message.version > 1 ? "var(--color-Grey1)" : "var(--color-Grey3)"}
                                    />
                                </Button>
                            </Tooltip>
                            <MessageVersionText>
                                {props.message.version} / {props.versionCount}
                            </MessageVersionText>
                            <Tooltip message={"다음 버전"}>
                                <Button
                                    onlyIcon
                                    bgColor={"transparent"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={() => {
                                        if (props.message.version < props.versionCount) {
                                            props.onChangeVersion(1);
                                        }
                                    }}
                                >
                                    <Icon
                                        name={"arrowRight"}
                                        size={"10"}
                                        color={
                                            props.message.version < props.versionCount
                                                ? "var(--color-Grey1)"
                                                : "var(--color-Grey3)"
                                        }
                                    />
                                </Button>
                            </Tooltip>
                        </MessageFooterLeft>
                        <MessageFooterRight>
                            {props.message.role === Constants.REVIEW_ROLE_HUMAN && (
                                <>
                                    {props.message.is_deleted ? (
                                        <>
                                            {isMessageEditing ? (
                                                <Tooltip message={"저장"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditMessageTextUpdate();
                                                        }}
                                                    >
                                                        <Icon name={"check"} size={"12"} color={"var(--color-Grey1)"} />
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip message={"메시지 재작성"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={() => {
                                                            setEditMessageText(props.message.content);
                                                            setIsMessageEditing(true);
                                                        }}
                                                    >
                                                        <Icon
                                                            name={"modify"}
                                                            size={"12"}
                                                            color={"var(--color-Grey3)"}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            <Tooltip message={"메시지 복원"}>
                                                <Button
                                                    onlyIcon
                                                    bgColor={"transparent"}
                                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                                    onClick={() => {
                                                        Confirm(
                                                            "warn",
                                                            "메시지 복원",
                                                            "이 메시지와 기존 하위 메시지를 복원하시겠습니까?",
                                                            "복원",
                                                            () => {
                                                                props.chatDelete(
                                                                    props.thread.id,
                                                                    props.message.id,
                                                                    true,
                                                                );
                                                            },
                                                        );
                                                    }}
                                                >
                                                    <Icon name={"refresh"} size={"12"} color={"var(--color-Grey1)"} />
                                                </Button>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <>
                                            {isMessageEditing ? (
                                                <Tooltip message={"저장"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditMessageTextUpdate();
                                                        }}
                                                    >
                                                        <Icon name={"check"} size={"12"} color={"var(--color-Grey1)"} />
                                                    </Button>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip message={"매시지 재작성"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={() => {
                                                            setEditMessageText(props.message.content);
                                                            setIsMessageEditing(true);
                                                        }}
                                                    >
                                                        <Icon
                                                            name={"modify"}
                                                            size={"12"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {Number(getMessageAuthorId(props.message)) ===
                                                Number(props.myProfileId) && (
                                                <Tooltip message={"메시지 삭제"}>
                                                    <Button
                                                        onlyIcon
                                                        bgColor={"transparent"}
                                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                                        onClick={() => {
                                                            Confirm(
                                                                "warn",
                                                                "메시지 삭제",
                                                                "메시지를 삭제하시겠습니까?\n하위 메시지도 볼 수 없게 됩니다.",
                                                                "삭제",
                                                                () => {
                                                                    props.chatDelete(
                                                                        props.thread.id,
                                                                        props.message.id,
                                                                        false,
                                                                    );
                                                                },
                                                            );
                                                        }}
                                                    >
                                                        <Icon
                                                            name={"delete"}
                                                            size={"12"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                            {props.message.role === Constants.REVIEW_ROLE_AI && (
                                <>
                                    <Tooltip message={"재응답"}>
                                        <Button
                                            onlyIcon
                                            bgColor={"transparent"}
                                            hoverBgColor={"var(--color-ButtonHover4)"}
                                            onClick={() => {
                                                let req_message = {
                                                    editMessageId: props.message.id,
                                                };
                                                props.chatStart(req_message);
                                            }}
                                        >
                                            <Icon name={"refresh"} size={"12"} color={"var(--color-Grey1)"} />
                                        </Button>
                                    </Tooltip>
                                </>
                            )}
                        </MessageFooterRight>
                    </>
                ) : (
                    // 검색된 메시지는 props.versionCount 가 없고, footer 를 비워둠
                    <></>
                )}
            </MessageFooter>
        </MessageContainer>
    );
}

export default PDFChatMessage;
